import React, { useRef, useState, useEffect } from 'react';
import { Text, Transformer, Group } from 'react-konva';

export default function TextElement({ 
  element, 
  isSelected, 
  isBeingEdited,
  onSelect, 
  onDragEnd, 
  onTransformEnd,
  onUpdate,
  canEdit 
}) {
  const textRef = useRef(null);
  const transformerRef = useRef(null);
  const [isEditing, setIsEditing] = useState(false);
  
  const props = element.properties || {};
  const text = props.text || 'Text';
  const fontSize = props.fontSize || 16;
  const fontFamily = props.fontFamily || 'Inter, system-ui, sans-serif';
  const fill = props.fill || '#FFFFFF';
  
  // Attach transformer when selected
  useEffect(() => {
    if (isSelected && transformerRef.current && textRef.current) {
      transformerRef.current.nodes([textRef.current]);
      transformerRef.current.getLayer().batchDraw();
    }
  }, [isSelected]);

  const handleDblClick = () => {
    if (!canEdit) return;
    setIsEditing(true);
    
    const textNode = textRef.current;
    const stage = textNode.getStage();
    const stageBox = stage.container().getBoundingClientRect();
    const absPos = textNode.getAbsolutePosition();
    const scale = stage.scaleX();
    
    const textarea = document.createElement('textarea');
    document.body.appendChild(textarea);
    
    textarea.value = text;
    textarea.style.position = 'absolute';
    textarea.style.left = `${stageBox.left + absPos.x * scale}px`;
    textarea.style.top = `${stageBox.top + absPos.y * scale}px`;
    textarea.style.width = `${Math.max(200, textNode.width() * scale)}px`;
    textarea.style.minHeight = `${textNode.height() * scale}px`;
    textarea.style.fontSize = `${fontSize * scale}px`;
    textarea.style.fontFamily = fontFamily;
    textarea.style.padding = '0';
    textarea.style.margin = '0';
    textarea.style.border = '2px solid #3B82F6';
    textarea.style.outline = 'none';
    textarea.style.resize = 'none';
    textarea.style.backgroundColor = 'white';
    textarea.style.color = fill;
    textarea.style.lineHeight = '1.2';
    textarea.style.zIndex = '1000';
    textarea.style.overflow = 'hidden';
    
    textarea.focus();
    textarea.select();
    
    const handleBlur = () => {
      const newText = textarea.value;
      document.body.removeChild(textarea);
      setIsEditing(false);
      
      if (newText !== text) {
        onUpdate(element.id, { 
          properties: { ...element.properties, text: newText } 
        });
      }
    };
    
    textarea.addEventListener('blur', handleBlur);
    textarea.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        textarea.blur();
      }
      // Allow enter for new line, shift+enter to submit
      if (e.key === 'Enter' && e.shiftKey) {
        e.preventDefault();
        textarea.blur();
      }
    });
    
    // Auto-resize
    textarea.addEventListener('input', () => {
      textarea.style.height = 'auto';
      textarea.style.height = textarea.scrollHeight + 'px';
    });
  };

  return (
    <>
      <Text
        ref={textRef}
        x={element.x}
        y={element.y}
        text={text}
        fontSize={fontSize}
        fontFamily={fontFamily}
        fill={fill}
        rotation={element.rotation || 0}
        scaleX={element.scale_x || 1}
        scaleY={element.scale_y || 1}
        draggable={canEdit}
        onClick={(e) => onSelect(element.id, e)}
        onTap={(e) => onSelect(element.id, e)}
        onDblClick={handleDblClick}
        onDblTap={handleDblClick}
        onDragStart={(e) => {
          e.cancelBubble = true;
        }}
        onDragEnd={(e) => {
          e.cancelBubble = true;
          onDragEnd(element.id, { x: e.target.x(), y: e.target.y() });
        }}
        onTransformEnd={() => {
          const node = textRef.current;
          const scaleX = node.scaleX();
          
          onTransformEnd(element.id, {
            x: node.x(),
            y: node.y(),
            properties: {
              ...element.properties,
              fontSize: Math.round(fontSize * scaleX)
            },
            rotation: node.rotation()
          });
          
          // Reset scale
          node.scaleX(1);
          node.scaleY(1);
        }}
        visible={!isEditing}
        // Add stroke for selection indication
        stroke={isSelected ? '#3B82F6' : (isBeingEdited ? isBeingEdited.color : 'transparent')}
        strokeWidth={isSelected || isBeingEdited ? 0.5 : 0}
      />
      
      {isSelected && canEdit && !isEditing && (
        <Transformer
          ref={transformerRef}
          enabledAnchors={['middle-left', 'middle-right']}
          boundBoxFunc={(oldBox, newBox) => {
            newBox.width = Math.max(30, newBox.width);
            return newBox;
          }}
          rotateEnabled={true}
        />
      )}
    </>
  );
}
